import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FaChevronDown, FaDownload, FaPlus, FaSyncAlt, FaUpload } from 'react-icons/fa'
import api, { apiUpload } from '../../../helpers/apiRequester'
import { Button, CollapsibleSection, EmptyState, Field, Input, Modal, Page, PageHeader, ResultLine, SearchInput, StatCard, StatGrid, StatusPill, Toolbar } from '../../../ui'
import styles from './styles.module.scss'

interface EmailChange {
    id: number
    email: string
    new_email: string | null
    replaced_at: string | null
}

interface Lender {
    normalised_name: string
    display_name: string
    email: string
    created_at: string | null
    updated_at: string | null
    previous_emails: EmailChange[]
}

interface LenderImportReport {
    sourceFile: string | null
    counts: {
        total: number
        added: number
        alreadyExists: number
        duplicateInFile: number
        invalid: number
    }
    added: { displayName: string; email: string }[]
    alreadyExists: {
        rowNum: number
        displayName: string
        csvEmail: string
        currentEmail: string | null
        emailMatches: boolean
    }[]
    duplicateInFile: { rowNum: number; displayName: string; firstRowNum: number }[]
    invalid: { rowNum: number; displayName: string | null; reason: string }[]
}

const formatDate = (value: string | null): string => {
    if (!value) return '—'
    const parsed = new Date(value)
    if (isNaN(parsed.getTime())) return value
    return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const formatDateTime = (value: string | null): string => {
    if (!value) return '—'
    const parsed = new Date(value)
    if (isNaN(parsed.getTime())) return value
    return parsed.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())

export const LenderEditsView = () => {
    const [lenders, setLenders] = useState<Lender[]>([])
    const [loaded, setLoaded] = useState(false)
    const [filter, setFilter] = useState('')
    const [editing, setEditing] = useState<Lender | null>(null)
    const [editEmail, setEditEmail] = useState('')
    const [saving, setSaving] = useState(false)
    const [expanded, setExpanded] = useState<Set<string>>(new Set())
    const [addOpen, setAddOpen] = useState(false)
    const [addName, setAddName] = useState('')
    const [addEmail, setAddEmail] = useState('')
    const [savingAdd, setSavingAdd] = useState(false)
    const [importing, setImporting] = useState(false)
    const [importReport, setImportReport] = useState<LenderImportReport | null>(null)
    const fileInputRef = useRef<HTMLInputElement | null>(null)

    const toggleExpanded = (normalisedName: string) => {
        setExpanded((current) => {
            const next = new Set(current)
            if (next.has(normalisedName)) {
                next.delete(normalisedName)
            } else {
                next.add(normalisedName)
            }
            return next
        })
    }

    const fetchLenders = async () => {
        Loading.circle('Loading lenders...')
        try {
            const response = await api.post('comms/lenders/list', {})
            if (!response.data?.success) {
                Report.failure('Error', response.data?.message || 'Could not load the lender directory.', 'Okay')
                return
            }
            setLenders(Array.isArray(response.data.lenders) ? response.data.lenders : [])
        } catch (error: any) {
            const message = error?.response?.data?.message || 'Could not load the lender directory.'
            Report.failure('Error', message, 'Okay')
        } finally {
            Loading.remove()
            setLoaded(true)
        }
    }

    useEffect(() => {
        fetchLenders()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const filteredLenders = useMemo(() => {
        const term = filter.trim().toLowerCase()
        if (!term) return lenders
        return lenders.filter((lender) => {
            const haystack = [lender.display_name, lender.normalised_name, lender.email, ...lender.previous_emails.flatMap((change) => [change.email, change.new_email])]
                .filter(Boolean)
                .join(' ')
                .toLowerCase()
            return haystack.includes(term)
        })
    }, [lenders, filter])

    const editedCount = useMemo(() => lenders.filter((lender) => lender.previous_emails.length > 0).length, [lenders])

    const openEdit = (lender: Lender) => {
        setEditing(lender)
        setEditEmail(lender.email || '')
    }

    const closeEdit = () => {
        if (saving) return
        setEditing(null)
        setEditEmail('')
    }

    const submitEdit = async () => {
        if (!editing) return
        const newEmail = editEmail.trim().toLowerCase()
        if (!isValidEmail(newEmail)) {
            Report.failure('Invalid email', 'Please enter a valid email address.', 'Okay')
            return
        }
        if (newEmail === (editing.email || '').trim().toLowerCase()) {
            Report.info('No change', 'The new email matches the current email.', 'Okay')
            return
        }
        setSaving(true)
        Loading.circle('Updating lender email...')
        try {
            const response = await api.post('comms/lenders/update-email', {
                normalisedName: editing.normalised_name,
                email: newEmail,
            })
            if (!response.data?.success) {
                Report.failure('Error', response.data?.message || 'Could not update the lender email.', 'Okay')
                return
            }
            setEditing(null)
            setEditEmail('')
            Report.success('Email updated', `${editing.display_name} is now ${newEmail}. The previous address has been archived.`, 'Okay')
            await fetchLenders()
        } catch (error: any) {
            const message = error?.response?.data?.message || 'Could not update the lender email.'
            Report.failure('Error', message, 'Okay')
        } finally {
            Loading.remove()
            setSaving(false)
        }
    }

    const openAdd = () => {
        setAddOpen(true)
        setAddName('')
        setAddEmail('')
    }

    const closeAdd = () => {
        if (savingAdd) return
        setAddOpen(false)
        setAddName('')
        setAddEmail('')
    }

    const submitAdd = async () => {
        const name = addName.trim()
        const email = addEmail.trim().toLowerCase()
        if (!name) {
            Report.failure('Missing name', 'Please enter the lender name.', 'Okay')
            return
        }
        if (!isValidEmail(email)) {
            Report.failure('Invalid email', 'Please enter a valid email address.', 'Okay')
            return
        }
        setSavingAdd(true)
        Loading.circle('Adding lender...')
        try {
            const response = await api.post('comms/lenders/add', { displayName: name, email })
            if (!response.data?.success) {
                Report.failure('Error', response.data?.message || 'Could not add the lender.', 'Okay')
                return
            }
            setAddOpen(false)
            setAddName('')
            setAddEmail('')
            Report.success('Lender added', `${name} has been added with ${email}.`, 'Okay')
            await fetchLenders()
        } catch (error: any) {
            const message = error?.response?.data?.message || 'Could not add the lender.'
            Report.failure('Error', message, 'Okay')
        } finally {
            Loading.remove()
            setSavingAdd(false)
        }
    }

    const exportCsv = () => {
        if (lenders.length === 0) {
            Report.info('Nothing to export', 'The lender directory is empty.', 'Okay')
            return
        }
        const escapeCsv = (value: string) => {
            const str = value ?? ''
            return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str
        }
        const rows = [['Lender', 'Email'], ...lenders.map((lender) => [lender.display_name, lender.email || ''])]
        const csv = rows.map((row) => row.map(escapeCsv).join(',')).join('\r\n')
        // BOM so Excel opens it as UTF-8 (lender names can contain accents).
        const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `comms-lenders-${new Date().toISOString().slice(0, 10)}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }

    const handleImportFile = async (file: File) => {
        setImporting(true)
        Loading.circle('Importing lenders...')
        try {
            const formData = new FormData()
            formData.append('file', file)
            const response = await apiUpload('comms/lenders/import', formData)
            if (!response.data?.success) {
                Report.failure('Error', response.data?.message || 'Could not import lenders.', 'Okay')
                return
            }
            setImportReport(response.data.report)
            await fetchLenders()
        } catch (error: any) {
            const message = error?.response?.data?.message || 'Could not import lenders.'
            Report.failure('Error', message, 'Okay')
        } finally {
            Loading.remove()
            setImporting(false)
            if (fileInputRef.current) fileInputRef.current.value = ''
        }
    }

    const mismatched = importReport?.alreadyExists.filter((row) => !row.emailMatches) ?? []

    return (
        <Page>
            <PageHeader
                eyebrow="Comms Management"
                title="Lenders"
                intro={
                    <>
                        The Comms Management lender directory. Editing a lender&apos;s email archives the old address to <strong>previous emails</strong> for our records. Bulk-add lenders with a CSV containing a <strong>Lender</strong> and an <strong>Email</strong> column — existing lenders are never overwritten by an import.
                    </>
                }
                actions={
                    <>
                        <Button icon={<FaSyncAlt />} onClick={fetchLenders}>
                            Refresh
                        </Button>
                        <Button icon={<FaDownload />} onClick={exportCsv} disabled={!loaded || lenders.length === 0}>
                            Export CSV
                        </Button>
                        <Button icon={<FaUpload />} onClick={() => fileInputRef.current?.click()} disabled={importing} loading={importing}>
                            Import CSV
                        </Button>
                        <Button variant="primary" icon={<FaPlus />} onClick={openAdd}>
                            Add lender
                        </Button>
                    </>
                }
            />

            <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                className={styles.hiddenFileInput}
                onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handleImportFile(file)
                }}
            />

            <StatGrid>
                <StatCard label="Total lenders" value={lenders.length} />
                <StatCard label="With email changes" value={editedCount} tone={editedCount ? 'accent' : 'default'} />
            </StatGrid>

            <div className={styles.stack}>
                <Toolbar end={<ResultLine>{filteredLenders.length} of {lenders.length} shown</ResultLine>}>
                    <SearchInput value={filter} onChange={setFilter} placeholder="Filter by lender name or email…" style={{ minWidth: 320 }} />
                </Toolbar>

                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Lender</th>
                                <th>Email</th>
                                <th>Changes</th>
                                <th>Created</th>
                                <th>Updated</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {filteredLenders.map((lender) => {
                                const changeCount = lender.previous_emails.length
                                const isExpanded = expanded.has(lender.normalised_name)
                                return (
                                    <React.Fragment key={lender.normalised_name}>
                                        <tr className={isExpanded ? styles.expandedRow : undefined}>
                                            <td>
                                                <div className={styles.lenderCell}>
                                                    <strong>{lender.display_name}</strong>
                                                    <span className={styles.mono}>{lender.normalised_name}</span>
                                                </div>
                                            </td>
                                            <td className={styles.emailCell}>{lender.email || '—'}</td>
                                            <td>
                                                {changeCount > 0 ? (
                                                    <Button size="sm" variant="ghost" iconRight={<FaChevronDown style={{ transform: isExpanded ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s ease' }} />} onClick={() => toggleExpanded(lender.normalised_name)} aria-expanded={isExpanded}>
                                                        <StatusPill tone="accent" round size="sm">
                                                            {changeCount}
                                                        </StatusPill>
                                                        {changeCount === 1 ? 'change' : 'changes'}
                                                    </Button>
                                                ) : (
                                                    <span className={styles.muted}>—</span>
                                                )}
                                            </td>
                                            <td className={styles.muted}>{formatDate(lender.created_at)}</td>
                                            <td className={styles.muted}>{formatDate(lender.updated_at)}</td>
                                            <td>
                                                <Button size="sm" onClick={() => openEdit(lender)}>
                                                    Edit
                                                </Button>
                                            </td>
                                        </tr>
                                        {isExpanded && changeCount > 0 && (
                                            <tr className={styles.historyRow}>
                                                <td colSpan={6}>
                                                    <div className={styles.timeline}>
                                                        <span className={styles.timelineTitle}>Email change history — {lender.display_name}</span>
                                                        <ul className={styles.timelineList}>
                                                            {lender.previous_emails.map((change) => (
                                                                <li key={change.id} className={styles.timelineItem}>
                                                                    <span className={styles.timelineDate}>
                                                                        {formatDateTime(change.replaced_at)}
                                                                        <StatusPill size="sm">#{change.id}</StatusPill>
                                                                    </span>
                                                                    <span className={styles.changeRow}>
                                                                        <span className={styles.oldEmail}>{change.email}</span>
                                                                        <span className={styles.changeArrow}>&rarr;</span>
                                                                        <span className={styles.newEmail}>{change.new_email || 'unknown'}</span>
                                                                    </span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                )
                            })}
                        </tbody>
                    </table>

                    {loaded && filteredLenders.length === 0 && (
                        <EmptyState
                            bare
                            title={lenders.length === 0 ? 'No lenders in the directory' : 'No lenders match your filter'}
                            description={lenders.length === 0 ? 'Add one, or import a CSV.' : undefined}
                            action={
                                lenders.length > 0 ? (
                                    <Button size="sm" onClick={() => setFilter('')}>
                                        Clear filter
                                    </Button>
                                ) : undefined
                            }
                        />
                    )}
                </div>
            </div>

            <Modal
                open={editing !== null}
                onClose={closeEdit}
                title="Edit lender email"
                subtitle={editing?.display_name}
                size="sm"
                busy={saving}
                footer={
                    <>
                        <Button onClick={closeEdit} disabled={saving}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={submitEdit} disabled={saving || !isValidEmail(editEmail)} loading={saving}>
                            Save
                        </Button>
                    </>
                }
            >
                <p className={styles.muted} style={{ margin: '0 0 12px' }}>
                    The current address will be archived under previous emails for our records.
                </p>
                <Field label="Current email">
                    <div className={styles.currentEmail}>{editing?.email || '—'}</div>
                </Field>
                <Field label="New email" required>
                    {(id) => (
                        <Input
                            id={id}
                            type="email"
                            value={editEmail}
                            onChange={(e) => setEditEmail(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') submitEdit()
                            }}
                            autoFocus
                        />
                    )}
                </Field>
            </Modal>

            <Modal
                open={addOpen}
                onClose={closeAdd}
                title="Add lender"
                subtitle="Available to batch imports straight away"
                size="sm"
                busy={savingAdd}
                footer={
                    <>
                        <Button onClick={closeAdd} disabled={savingAdd}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={submitAdd} disabled={savingAdd || !addName.trim() || !isValidEmail(addEmail)} loading={savingAdd}>
                            Add lender
                        </Button>
                    </>
                }
            >
                <Field label="Lender name" required>
                    {(id) => <Input id={id} type="text" value={addName} onChange={(e) => setAddName(e.target.value)} placeholder="e.g. Big Bank Finance Ltd" autoFocus />}
                </Field>
                <Field label="Email" required>
                    {(id) => (
                        <Input
                            id={id}
                            type="email"
                            value={addEmail}
                            onChange={(e) => setAddEmail(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') submitAdd()
                            }}
                            placeholder="e.g. complaints@bigbank.co.uk"
                        />
                    )}
                </Field>
            </Modal>

            <Modal
                open={importReport !== null}
                onClose={() => setImportReport(null)}
                title="Import complete"
                subtitle={
                    importReport
                        ? importReport.sourceFile
                            ? `Processed ${importReport.sourceFile} — ${importReport.counts.total} ${importReport.counts.total === 1 ? 'row' : 'rows'}.`
                            : `Processed ${importReport.counts.total} rows.`
                        : undefined
                }
                size="lg"
                footer={
                    <Button variant="primary" onClick={() => setImportReport(null)}>
                        Done
                    </Button>
                }
            >
                {importReport && (
                    <>
                        <StatGrid minColumn={120}>
                            <StatCard compact label="Added" value={importReport.counts.added} tone={importReport.counts.added ? 'good' : 'default'} />
                            <StatCard compact label="Already existed" value={importReport.counts.alreadyExists} />
                            <StatCard compact label="Dup in file" value={importReport.counts.duplicateInFile} />
                            <StatCard compact label="Invalid" value={importReport.counts.invalid} tone={importReport.counts.invalid ? 'bad' : 'default'} />
                        </StatGrid>

                        <div className={styles.reportBody}>
                            {importReport.added.length > 0 && (
                                <CollapsibleSection title="Added" meta={`${importReport.added.length}`} variant="panel" defaultOpen>
                                    <ul className={styles.reportList}>
                                        {importReport.added.map((row) => (
                                            <li key={row.displayName}>
                                                <strong>{row.displayName}</strong> — {row.email}
                                            </li>
                                        ))}
                                    </ul>
                                </CollapsibleSection>
                            )}

                            {mismatched.length > 0 && (
                                <CollapsibleSection title="Already existed with a different email" meta={`${mismatched.length}`} variant="panel" defaultOpen>
                                    <p className={styles.muted} style={{ margin: '0 0 10px' }}>
                                        These were not changed. Use Edit on the lender if the new address is correct, so the old one is archived.
                                    </p>
                                    <ul className={styles.reportList}>
                                        {mismatched.map((row) => (
                                            <li key={`${row.rowNum}-${row.displayName}`}>
                                                <strong>{row.displayName}</strong> — currently {row.currentEmail || 'no email'}, CSV has {row.csvEmail}
                                            </li>
                                        ))}
                                    </ul>
                                </CollapsibleSection>
                            )}

                            {importReport.duplicateInFile.length > 0 && (
                                <CollapsibleSection title="Duplicates in file" meta={`${importReport.duplicateInFile.length}`} variant="panel">
                                    <ul className={styles.reportList}>
                                        {importReport.duplicateInFile.map((row) => (
                                            <li key={`${row.rowNum}-${row.displayName}`}>
                                                Row {row.rowNum}: <strong>{row.displayName}</strong> repeats row {row.firstRowNum} — first occurrence kept
                                            </li>
                                        ))}
                                    </ul>
                                </CollapsibleSection>
                            )}

                            {importReport.invalid.length > 0 && (
                                <CollapsibleSection title="Invalid rows" meta={`${importReport.invalid.length}`} variant="panel" tone="bad" defaultOpen>
                                    <ul className={styles.reportList}>
                                        {importReport.invalid.map((row) => (
                                            <li key={`${row.rowNum}-${row.reason}`}>
                                                Row {row.rowNum}
                                                {row.displayName ? (
                                                    <>
                                                        {' '}
                                                        (<strong>{row.displayName}</strong>)
                                                    </>
                                                ) : null}
                                                : {row.reason}
                                            </li>
                                        ))}
                                    </ul>
                                </CollapsibleSection>
                            )}
                        </div>
                    </>
                )}
            </Modal>
        </Page>
    )
}
